import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Всплывающая панель, привязанная к кнопке и не уезжающая за край экрана.
 *
 * Заведено после двух одинаковых дефектов подряд: сначала обрезалась справка
 * о слоях внутри панели карты, потом — список языков внутри выезжающего меню
 * на телефоне. Оба раза причина одна и та же и не в счёте координат:
 * у контейнера `overflow-hidden` (или `overflow-y-auto`, который клипует
 * и по горизонтали), а из-под него абсолютно позиционированный потомок
 * не выбирается никаким сдвигом.
 *
 * Отсюда правило: панель рисуется порталом в `body` и позиционируется
 * в координатах экрана. Обрезать её тогда некому, а держать в пределах
 * экрана — задача этого хука.
 *
 * Возвращает `style` целиком: пока положение не посчитано, панель скрыта —
 * иначе первый кадр рисовал бы её в левом верхнем углу.
 */

/** Отступ от края экрана: панель, притёртая к самому краю, читается как обрезанная. */
export const EDGE_GAP = 8;

/** Просвет между кнопкой и панелью. */
const GAP = 4;

export function useAnchoredPanel<A extends HTMLElement, P extends HTMLElement>(
  open: boolean,
): { anchor: RefObject<A | null>; panel: RefObject<P | null>; style: CSSProperties } {
  const anchor = useRef<A>(null);
  const panel = useRef<P>(null);

  const [at, setAt] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }

    const place = (): void => {
      const from = anchor.current?.getBoundingClientRect();
      const size = panel.current?.getBoundingClientRect();

      if (from === undefined || size === undefined) {
        return;
      }

      /*
        Хочется прижать панель к правому краю кнопки — так она не наезжает
        на то, что правее. Но если от этого она вылезает за любой из краёв
        экрана, её двигают внутрь: панель, которую не прочитать, хуже панели,
        закрывшей соседнюю кнопку.
      */
      const left = Math.min(
        Math.max(from.right - size.width, EDGE_GAP),
        Math.max(window.innerWidth - size.width - EDGE_GAP, EDGE_GAP),
      );

      /*
        Обычно под кнопкой. Но у кнопки в нижней части экрана — а на телефоне
        и переключатель языка, и выход стоят внизу меню — панель уходит
        за нижний край, и прокрутить до неё нельзя: она зафиксирована. Тогда
        она встаёт над кнопкой; если и сверху не влезает, прижимается к краю.
      */
      const below = from.bottom + GAP;
      const above = from.top - GAP - size.height;

      const top =
        below + size.height + EDGE_GAP <= window.innerHeight
          ? below
          : above >= EDGE_GAP
            ? above
            : Math.max(window.innerHeight - size.height - EDGE_GAP, EDGE_GAP);

      setAt({ left, top });
    };

    place();

    /*
      Панель зафиксирована в координатах экрана, а кнопка едет вместе
      со своим списком: без пересчёта панель отрывается от того, к чему
      относится. `capture` — потому что прокручивается не окно, а панель
      внутри него.
    */
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);

    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  return {
    anchor,
    panel,
    style: {
      position: 'fixed',
      left: at?.left ?? 0,
      top: at?.top ?? 0,
      visibility: at === null ? 'hidden' : 'visible',
    },
  };
}

/**
 * Нажатие мимо панели.
 *
 * Панель живёт в `body`, а не внутри кнопки, поэтому спрашиваются обе:
 * иначе нажатие по самой панели считалось бы «мимо» и закрывало её.
 */
export function outside(
  target: EventTarget | null,
  ...boxes: RefObject<HTMLElement | null>[]
): boolean {
  return boxes.every((box) => !box.current?.contains(target as Node));
}
